// SubscriptionManagementModal: Netflix-style subscription management
// Comprehensive billing and subscription interface with Stripe integration

import React, { useState } from 'react'
import { Modal } from '../ui/Modal'
import { useSubscription } from '@/hooks/useSubscription'
import { useCredits } from '@/hooks/useCredits'
import { CreditBalance } from '../billing/CreditBalance'
import {
  CreditCard,
  Crown,
  Activity,
  Calendar,
  Receipt,
  ExternalLink,
  TrendingUp,
  AlertCircle
} from 'lucide-react'

interface SubscriptionManagementModalProps {
  isOpen: boolean
  onClose: () => void
  onUpgrade?: () => void
}

export function SubscriptionManagementModal({
  isOpen,
  onClose,
  onUpgrade
}: SubscriptionManagementModalProps) {
  const {
    subscription,
    isLoading,
    error,
    isSubscribed,
    isPremium,
    openCustomerPortal
  } = useSubscription()

  const { balance, isLoading: creditsLoading } = useCredits()
  const [isOpeningPortal, setIsOpeningPortal] = useState(false)
  const [portalError, setPortalError] = useState<string | null>(null)

  const handleManageBilling = async () => {
    try {
      setIsOpeningPortal(true)
      setPortalError(null)
      await openCustomerPortal()
    } catch (error) {
      console.error('Failed to open customer portal:', error)
      setPortalError(
        error instanceof Error
          ? error.message
          : 'Unable to access billing portal. Please contact support if this issue persists.'
      )
    } finally {
      setIsOpeningPortal(false)
    }
  }

  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade()
    }
    onClose()
  }

  const getPlanBadgeColor = (planName?: string) => {
    switch (planName?.toLowerCase()) {
      case 'enterprise':
        return 'bg-purple-100 text-purple-800 border-purple-200'
      case 'business':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'professional':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'starter':
        return 'bg-orange-100 text-orange-800 border-orange-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getUsagePercentage = () => {
    if (!balance) return 0
    const used = balance.used_this_month || 0
    const total = subscription?.credits_per_month || 1
    return Math.min((used / total) * 100, 100)
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Manage Subscription"
      subtitle="Billing, plans, and payment methods"
      maxWidth="2xl"
    >
      <div className="p-6">
        {isLoading || creditsLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="h-20 bg-gray-200 rounded-lg"></div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-3" />
            <div>
              <p className="text-red-800 font-medium">Unable to load subscription data</p>
              <p className="text-red-600 text-sm mt-1">{error}</p>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Portal Error Message */}
            {portalError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
                <AlertCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0" />
                <div>
                  <p className="text-red-800 font-medium">Billing Portal Access Issue</p>
                  <p className="text-red-600 text-sm mt-1">{portalError}</p>
                  {!isSubscribed && (
                    <p className="text-red-600 text-sm mt-2">
                      Note: You need an active paid subscription to access the billing portal.
                    </p>
                  )}
                </div>
                <button
                  onClick={() => setPortalError(null)}
                  className="ml-auto text-red-600 hover:text-red-800"
                >
                  ×
                </button>
              </div>
            )}

            {/* Current Plan Overview */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 border">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <Crown className="w-6 h-6 text-blue-600" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Current Plan</h3>
                    <p className="text-gray-600 text-sm">Your subscription details</p>
                  </div>
                </div>
                <div className={`px-3 py-1 rounded-full text-sm font-medium border ${getPlanBadgeColor(subscription?.plan_name)}`}>
                  {subscription?.plan_name || 'Free Trial'}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-lg p-4 border">
                  <div className="flex items-center space-x-2 mb-2">
                    <Activity className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium text-gray-700">Credits</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {balance?.balance?.toLocaleString() || '0'}
                  </p>
                  <p className="text-xs text-gray-600">
                    {subscription?.credits_per_month?.toLocaleString() || '0'} per month
                  </p>
                </div>

                <div className="bg-white rounded-lg p-4 border">
                  <div className="flex items-center space-x-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-gray-700">Usage</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {getUsagePercentage().toFixed(0)}%
                  </p>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${getUsagePercentage()}%` }}
                    />
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4 border">
                  <div className="flex items-center space-x-2 mb-2">
                    <Calendar className="w-4 h-4 text-purple-600" />
                    <span className="text-sm font-medium text-gray-700">Status</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900 capitalize">
                    {subscription?.status || 'Free'}
                  </p>
                  <p className="text-xs text-gray-600">
                    {isSubscribed ? 'Subscription active' : 'No subscription'}
                  </p>
                </div>
              </div>
            </div>

            {/* Credit Balance Details */}
            <CreditBalance />

            {/* Action Buttons */}
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={handleManageBilling}
                  disabled={isOpeningPortal}
                  className="flex items-center justify-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title={!isSubscribed ? "Requires active subscription" : "Open Stripe billing portal"}
                >
                  <CreditCard className="w-5 h-5" />
                  <span>{isOpeningPortal ? 'Opening...' : 'Manage Billing'}</span>
                  <ExternalLink className="w-4 h-4" />
                </button>

                {!isPremium && (
                  <button
                    onClick={handleUpgrade}
                    className="flex items-center justify-center space-x-2 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Crown className="w-5 h-5" />
                    <span>Upgrade Plan</span>
                  </button>
                )}
              </div>

              {/* Billing Portal Info */}
              <div className={`rounded-lg p-4 border ${
                isSubscribed
                  ? 'bg-gray-50 border-gray-200'
                  : 'bg-yellow-50 border-yellow-200'
              }`}>
                <div className="flex items-start space-x-3">
                  <Receipt className={`w-5 h-5 mt-0.5 ${
                    isSubscribed ? 'text-gray-600' : 'text-yellow-600'
                  }`} />
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">Billing Portal</h4>
                    <p className="text-sm text-gray-600">
                      {isSubscribed
                        ? 'Access your complete billing history, update payment methods, download invoices, and manage your subscription through our secure Stripe portal.'
                        : 'The billing portal is available for users with active paid subscriptions. Upgrade to a paid plan to access billing management features.'
                      }
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Plan Comparison Teaser */}
            {!isPremium && (
              <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-4 border border-green-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900">Unlock More Features</h4>
                    <p className="text-sm text-gray-600">
                      Get more credits, priority support, and advanced features with a paid plan.
                    </p>
                  </div>
                  <button
                    onClick={handleUpgrade}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                  >
                    View Plans
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  )
}

export default SubscriptionManagementModal